/**
 * @file ProductsPagination.tsx
 * @description Постраничный вывод реестра товаров (phase19.31, макет (а)):
 * «1–50 из 312» · ‹ «1 из 7» › · «По 50 ▾».
 *
 * Повторяет `OrdersPagination` (стандартные кнопки темы и `SelectField`), но
 * со своими размерами страницы и подписями: тот привязан к заказам. Когда
 * постраничный вывод понадобится третьему реестру — вынести в `components/ui`.
 */

import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "../../ui/button";
import { SelectField } from "../../ui/SelectField";
import { PRODUCTS_PAGE_SIZES } from "../../../lib/catalog-registry-query";

const SIZE_OPTIONS = PRODUCTS_PAGE_SIZES.map((size) => ({ value: String(size), label: `По ${size}` }));

interface Props {
  page: number;
  pages: number;
  from: number;
  to: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export function ProductsPagination({ page, pages, from, to, total, pageSize, onPageChange, onPageSizeChange }: Props) {
  if (total === 0) return null;
  return (
    <nav aria-label="Страницы реестра товаров" className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <span className="type-body tabular-nums text-on-surface-variant">
        {from}–{to} из {total}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon-sm" onClick={() => onPageChange(page - 1)} disabled={page <= 1} aria-label="Предыдущая страница">
          <ChevronLeft className="size-4" />
        </Button>
        <span className="type-body min-w-12 text-center tabular-nums" aria-current="page">
          {page} из {pages}
        </span>
        <Button variant="ghost" size="icon-sm" onClick={() => onPageChange(page + 1)} disabled={page >= pages} aria-label="Следующая страница">
          <ChevronRight className="size-4" />
        </Button>
        <SelectField
          ariaLabel="Товаров на странице"
          value={String(pageSize)}
          onChange={(value) => onPageSizeChange(Number(value))}
          options={SIZE_OPTIONS}
          className="w-[120px]"
        />
      </div>
    </nav>
  );
}
